/**
 * POS V2 — Phase C (shell main-15 + drawers + offline syncEngine)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import syncEngine from '../services/syncEngine';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import PosV2Header from '../features/pos-v2/PosV2Header';
import PosV2OfflineBanner from '../features/pos-v2/PosV2OfflineBanner';
import PosV2CaisseFooter from '../features/pos-v2/PosV2CaisseFooter';
import { PosV2CatalogGrid } from '../features/pos-v2/PosV2CatalogGrid';
import PosV2CartPanel from '../features/pos-v2/PosV2CartPanel';
import PosV2CheckoutPanel from '../features/pos-v2/PosV2CheckoutPanel';
import PosV2Drawer from '../features/pos-v2/PosV2Drawer';
import PosV2ProductDrawer from '../features/pos-v2/PosV2ProductDrawer';
import PosV2SavDrawer from '../features/pos-v2/PosV2SavDrawer';
import PosV2HorsStockDrawer from '../features/pos-v2/PosV2HorsStockDrawer';
import PosV2CommandesDrawer from '../features/pos-v2/PosV2CommandesDrawer';
import PosV2ClientDrawer from '../features/pos-v2/PosV2ClientDrawer';
import PosV2TicketsDrawer from '../features/pos-v2/PosV2TicketsDrawer';
import PosV2EncaissementMagModal from '../features/pos-v2/PosV2EncaissementMagModal';
import PosV2PrintTicketModal from '../features/pos-v2/PosV2PrintTicketModal';
import { fmt, POS_V2_DEFAULT_KEY, readSessionMag, resolvePosCatalogPrice } from '../features/pos-v2/posV2Utils';
import { isPrimaryAdmin } from '../utils/roleRouting';

const REMISE_MAX_PCT = 20;

/** Détecte le refus serveur 403 REMISE_PLAFONNEE (detail string ou objet {code}). */
const isRemisePlafonneeError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail.toUpperCase().includes('REMISE_PLAFONNEE');
  if (detail && typeof detail === 'object') {
    const code = String(detail.code || detail.error || detail.detail || '').toUpperCase();
    return code.includes('REMISE_PLAFONNEE');
  }
  return false;
};

const normMag = (m) => {
  if (typeof m === 'string' && m.trim()) return m.trim().toUpperCase();
  if (Array.isArray(m) && typeof m[0] === 'string' && m[0].trim()) return m[0].trim().toUpperCase();
  return 'M1';
};

/** Message d'erreur TOUJOURS sous forme de CHAÎNE. Le serveur peut renvoyer `detail` en OBJET
 *  ({code,message}) — notamment 403 MAGASIN_INCOMPATIBLE / 409 MAGASIN_NON_SELECTIONNE sous isolation.
 *  Rendre un objet dans du JSX casse React (« Objects are not valid as a React child ») => ÉCRAN NOIR.
 *  Ce helper garantit qu'un 401/403/409/500 produit une chaîne lisible, jamais un objet. */
const errMessage = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.message || d.detail || d.code || fallback;
  return err?.message || fallback;
};

const PosV2Page = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const { currentSessionMagasin, setSessionMagasin, allMagasins } = usePermissions();
  const offline = useOfflineStatus();
  const sessionMag = normMag(currentSessionMagasin || readSessionMag(user?.default_magasin_id));
  const [activeMode, setActiveMode] = useState('vente');
  const [drawer, setDrawer] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [catalogPhotoBust, setCatalogPhotoBust] = useState(0);
  const [caisse, setCaisse] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [health, setHealth] = useState(null);
  const [query, setQuery] = useState('');
  const [catalogMag, setCatalogMag] = useState(() => readSessionMag(user?.default_magasin_id));
  const [catalog, setCatalog] = useState({ items: [], total: 0, page: 1, limit: 48 });
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [cart, setCart] = useState([]);
  const [lineBadges, setLineBadges] = useState({});
  const [modePaiement, setModePaiement] = useState('Espèces');
  const [paiementEspeces, setPaiementEspeces] = useState('');
  const [paiementCb, setPaiementCb] = useState('');
  const [avoirRef, setAvoirRef] = useState('');
  const [avoirLookup, setAvoirLookup] = useState(null);
  const [avoirSuggestions, setAvoirSuggestions] = useState([]);
  const [avoirLoading, setAvoirLoading] = useState(false);
  const [paiementAvoir, setPaiementAvoir] = useState('');
  const [complementMode, setComplementMode] = useState('Espèces');
  const [clientNom, setClientNom] = useState('Client POS V2');
  const [clientTel, setClientTel] = useState('');
  const [clientLoyalty, setClientLoyalty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [error, setError] = useState('');
  const [sumupPending, setSumupPending] = useState(null);
  const [sumupStatus, setSumupStatus] = useState(null);
  // PayPal (encaissement « 4× ») : ordre créé (approbation client) -> capturé -> finalisation vente.
  const [paypalPending, setPaypalPending] = useState(null); // {order_id, approve_url, amount}
  const [paypalStatus, setPaypalStatus] = useState(null);   // CREATED | APPROVED | COMPLETED
  const [paypalBusy, setPaypalBusy] = useState(false);
  const [posV2Preferred, setPosV2Preferred] = useState(() => {
    try { return localStorage.getItem(POS_V2_DEFAULT_KEY) === '1'; } catch { return false; }
  });
  const [remiseType, setRemiseType] = useState('pct');
  const [remiseValue, setRemiseValue] = useState(0);
  const [loyaltyRewardApplied, setLoyaltyRewardApplied] = useState(false);
  const [showMagConfirm, setShowMagConfirm] = useState(false);
  const [showPrintPrompt, setShowPrintPrompt] = useState(false);
  const [selectedSaleMagasin, setSelectedSaleMagasin] = useState('M1');
  const [caisseByMag, setCaisseByMag] = useState({});
  const [loadingCaisseByMag, setLoadingCaisseByMag] = useState(false);
  const carlosUser = isPrimaryAdmin(user);

  const subtotal = useMemo(() => cart.reduce((s, l) => s + Math.max(0, Number(l.prix_total || 0) - Number(l.rabais_degat || 0)), 0), [cart]);
  // Plafond remise vendeur (hors Carlos) : 20 % du sous-total, en % comme en €.
  const clampRemise = useCallback((raw, type = remiseType, base = subtotal) => {
    const value = Math.max(0, Number(raw) || 0);
    if (carlosUser) return value;
    if (type === 'pct') return Math.min(REMISE_MAX_PCT, value);
    const cap = Math.round(base * (REMISE_MAX_PCT / 100) * 100) / 100;
    return Math.min(cap, value);
  }, [carlosUser, remiseType, subtotal]);
  const setRemiseValueClamped = (raw) => {
    setRemiseValue(clampRemise(raw));
  };
  // Re-clamp à chaque changement de sous-total / type : retirer un article ne
  // doit jamais laisser une remise € au-dessus de 20 %.
  useEffect(() => {
    if (carlosUser) return;
    setRemiseValue((prev) => {
      const next = clampRemise(prev);
      return next === (Number(prev) || 0) ? prev : next;
    });
  }, [subtotal, remiseType, carlosUser, clampRemise]);
  const remiseAmount = useMemo(() => {
    const value = Number(remiseValue) || 0;
    if (value <= 0) return 0;
    if (remiseType === 'pct') return subtotal * (value / 100);
    return Math.min(value, subtotal);
  }, [subtotal, remiseType, remiseValue]);
  const total = useMemo(() => Math.max(0, subtotal - remiseAmount), [subtotal, remiseAmount]);

  const applyAvoirLookup = useCallback((data) => {
    if (!data?.ref) return;
    setAvoirLookup(data);
    setAvoirRef(data.ref);
    setAvoirSuggestions([]);
    setError('');
    setPaiementAvoir(String(Math.min(Number(data.solde) || 0, total)));
    setClientNom((prev) => {
      if (data.client_nom && (!prev || prev === 'Client POS V2')) return data.client_nom;
      return prev;
    });
    if (data.client_tel) {
      setClientTel((prev) => prev || data.client_tel);
    }
  }, [total]);
  const crossMagasin = useMemo(() => {
    const owners = new Set(cart.map((l) => l.stock_magasin_id).filter(Boolean));
    return owners.size > 1 || [...owners].some((m) => m !== sessionMag);
  }, [cart, sessionMag]);

  const cartStockByMag = useMemo(() => {
    const map = {};
    cart.forEach((l) => {
      if (l.is_acompte || l.is_hors_stock || l.produit_ref?.startsWith('ACOMPTE-')) return;
      const mag = (l.stock_magasin_id || sessionMag || '').toUpperCase();
      if (!mag) return;
      map[mag] = (map[mag] || 0) + Number(l.prix_total || 0);
    });
    return map;
  }, [cart, sessionMag]);

  const fetchCaisse = useCallback(async () => {
    try {
      const res = await api.get('/caisse/status', { params: { magasin_id: sessionMag } });
      setCaisse(res.data);
    } catch {
      setCaisse({ is_open: false });
    }
  }, [api, sessionMag]);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/caisse/pos-dashboard', { params: { magasin_id: sessionMag } });
      setDashboard(res.data);
    } catch {
      setDashboard(null);
    }
  }, [api, sessionMag]);

  const loadCatalog = useCallback(async (page = 1) => {
    setLoadingCatalog(true);
    setError('');
    try {
      const params = {
        page,
        limit: catalog.limit || 48,
      };
      if (catalogMag && catalogMag !== 'ALL') {
        params.session_magasin = catalogMag;
        params.in_magasin = catalogMag;
      } else {
        params.session_magasin = sessionMag;
      }
      if (query.trim().length >= 2) params.q = query.trim();
      const res = await api.get('/stock/catalog', { params });
      setCatalog(res.data || { items: [], total: 0, page: 1, limit: 48 });
    } catch (err) {
      // Un refus d'isolation (403 MAGASIN_INCOMPATIBLE, 409, 401) ne doit JAMAIS casser la page :
      // message clair, catalogue vidé, POS toujours utilisable.
      setCatalog({ items: [], total: 0, page: 1, limit: catalog.limit || 48 });
      setError(errMessage(err, t('posV2.catalogUnavailable')));
    } finally {
      setLoadingCatalog(false);
    }
  }, [api, query, catalogMag, catalog.limit, sessionMag, t]);

  // COHÉRENCE des deux sélecteurs (session active vs catalogue POS) : un sélecteur local ne doit JAMAIS
  // demander le catalogue d'une AUTRE boutique en conservant la session courante (cela déclenchait un 403
  // MAGASIN_INCOMPATIBLE -> écran noir). Choisir une autre boutique = CHANGER LA SESSION (jeton actualisé),
  // avec avertissement si le panier n'est pas vide (aucune opération/paiement en cours n'est déplacé : le
  // panier est vidé à la bascule). « Tous » et la boutique active restent un simple filtre d'affichage.
  const selectCatalogShop = useCallback((code) => {
    if (code && code !== 'ALL' && code !== sessionMag) {
      if (cart.length > 0) {
        const msg = `Panier en cours sur ${sessionMag}. Changer la boutique active vers ${code} videra le panier et actualisera la session. Continuer ?`;
        if (!window.confirm(msg)) return;
      }
      setSessionMagasin(code);  // bascule session + jeton ; l'effet sync catalogMag et vide le panier
      return;
    }
    setCatalogMag(code);
  }, [sessionMag, cart.length, setSessionMagasin]);

  const classifyCart = useCallback(async (lines) => {
    if (!lines.length) {
      setLineBadges({});
      return;
    }
    try {
      const res = await api.post('/pos/classify-cart-lines', {
        ref_vente: 'POS-DRAFT',
        items: lines.map((l) => ({
          produit_ref: l.produit_ref,
          designation: l.designation,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          prix_total: l.prix_total,
          stock_magasin_id: l.stock_magasin_id,
        })),
        vente_net: lines.reduce((s, l) => s + l.prix_total, 0),
      });
      const map = {};
      (res.data?.lines || []).forEach((ln) => { map[ln.produit_ref] = ln.ctype; });
      setLineBadges(map);
    } catch {
      setLineBadges({});
    }
  }, [api]);

  useEffect(() => {
    if (!currentSessionMagasin && user?.default_magasin_id) {
      setSessionMagasin(normMag(user.default_magasin_id));
    }
  }, [user, currentSessionMagasin, setSessionMagasin]);

  const handleSessionMagChange = useCallback((code) => {
    setSessionMagasin(code);
  }, [setSessionMagasin]);
  useEffect(() => {
    setCatalogMag((prev) => prev || readSessionMag(user?.default_magasin_id));
  }, [user]);
  useEffect(() => {
    fetchCaisse();
    fetchDashboard();
    api.get('/pos/v2/health').then((r) => setHealth(r.data)).catch(() => null);
  }, [api, fetchCaisse, fetchDashboard, sessionMag]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadCatalog(1);
  }, [catalogMag]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (query.trim().length < 2) return undefined;
    const timer = setTimeout(() => loadCatalog(1), 450);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevSessionMagRef = React.useRef(sessionMag);
  useEffect(() => {
    setCatalogMag((prev) => (prev === 'ALL' ? 'ALL' : sessionMag));
    // ISOLATION — à CHAQUE changement de boutique active, vider le PANIER et tout l'état d'encaissement :
    // aucun panier / total / cache d'une boutique ne doit être réutilisé dans une autre (M1 -> M2 -> M1).
    if (prevSessionMagRef.current && sessionMag && prevSessionMagRef.current !== sessionMag) {
      setCart([]);
      setLineBadges({});
      setClientNom('Client POS V2');
      setClientTel('');
      setClientLoyalty(null);
      setModePaiement('Espèces');
      setPaiementEspeces('');
      setPaiementCb('');
      setPaiementAvoir('');
      setAvoirRef('');
      setAvoirLookup(null);
      setAvoirSuggestions([]);
      setRemiseType('pct');
      setRemiseValue(0);
      setLoyaltyRewardApplied(false);
      setSumupPending(null);
      setSumupStatus(null);
      setPaypalPending(null);
      setPaypalStatus(null);
      setError('');
    }
    prevSessionMagRef.current = sessionMag;
  }, [sessionMag]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { classifyCart(cart); }, [cart, classifyCart]);

  useEffect(() => {
    if (modePaiement !== 'Avoir') {
      setAvoirSuggestions([]);
      return undefined;
    }
    const q = (avoirRef || '').trim();
    if (q.length < 2 || (avoirLookup && avoirLookup.ref === q)) {
      if (q.length < 2) setAvoirSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/avoirs/lookup', { params: { q } });
        if (cancelled) return;
        if (Array.isArray(res.data?.matches)) setAvoirSuggestions(res.data.matches);
        else if (res.data?.ref) setAvoirSuggestions([res.data]);
        else setAvoirSuggestions([]);
      } catch {
        if (!cancelled) setAvoirSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [avoirRef, modePaiement, api, avoirLookup]);

  useEffect(() => {
    if (!sumupPending?.checkout_id || sumupStatus === 'PAID_PENDING' || sumupStatus === 'PAID_APPLIED' || !offline.online) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/pos/v2/sumup/status/${sumupPending.checkout_id}`);
        if (cancelled) return;
        const st = res.data?.status || res.data?.sumup_status;
        if (st) {
          setSumupStatus(st);
          if (st === 'FAILED') {
            setError(res.data?.failure_hint || t('posV2.paymentRefusedTpe'));
          }
        }
      } catch {
        /* ignore poll errors */
      }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [api, offline.online, sumupPending?.checkout_id, sumupStatus, t]);

  const handlePhotoUpdated = useCallback((data) => {
    if (data?.reference) {
      setProductDetail((prev) => (prev?.reference === data.reference ? { ...prev, ...data } : prev));
      setCatalog((prev) => ({
        ...prev,
        items: (prev.items || []).map((it) => (
          it.reference === data.reference
            ? { ...it, thumbnail_url: data.thumbnail_url, photo_count: data.photo_count }
            : it
        )),
      }));
    }
    setCatalogPhotoBust(Date.now());
    loadCatalog(catalog.page || 1);
  }, [catalog.page, loadCatalog]);

  const togglePosV2Preferred = () => {
    const next = !posV2Preferred;
    setPosV2Preferred(next);
    try {
      if (next) localStorage.setItem(POS_V2_DEFAULT_KEY, '1');
      else localStorage.removeItem(POS_V2_DEFAULT_KEY);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    // CB (terminal autonome, ex. magasin M2) et Espèces ne nécessitent pas internet ;
    // seuls SumUp / Mixte (checkout en ligne) sont rabattus sur Espèces hors-ligne.
    if (!offline.online && modePaiement !== 'Espèces' && modePaiement !== 'CB') setModePaiement('Espèces');
  }, [offline.online, modePaiement]);

  const handleModeChange = (mode) => {
    setActiveMode(mode);
    if (mode === 'reparation' || mode === 'acompte') setDrawer('sav');
    else if (mode === 'hors_stock') setDrawer('hors_stock');
    else if (mode === 'commandes') setDrawer('commandes');
    else if (mode === 'client') setDrawer('client');
    else setDrawer(null);
  };

  const addLineToCart = (line) => {
    const ref = line.produit_ref;
    const stockMag = line.stock_magasin_id;
    setCart((prev) => {
      if (line.is_acompte || line.is_hors_stock || ref.startsWith('ACOMPTE-') || ref === 'HORS-STOCK') {
        return [...prev, {
          ...line,
          stock_magasin_id: line.stock_magasin_id ?? null,
          lineType: ref.startsWith('ACOMPTE-CMD') ? 'commandes' : line.is_hors_stock || ref === 'HORS-STOCK' ? 'ventes' : 'reparations',
        }];
      }
      const idx = prev.findIndex((l) => l.produit_ref === ref && l.stock_magasin_id === stockMag);
      if (idx >= 0) {
        const next = [...prev];
        const qty = next[idx].quantite + (line.quantite || 1);
        next[idx] = {
          ...next[idx],
          quantite: qty,
          prix_total: qty * next[idx].prix_unitaire,
          thumbnail_url: next[idx].thumbnail_url || line.thumbnail_url || null,
        };
        return next;
      }
      return [...prev, { ...line, quantite: line.quantite || 1, prix_total: line.prix_total || line.prix_unitaire }];
    });
    setActiveMode('vente');
    setDrawer(null);
  };

  const addFromCatalog = (item) => {
    const localQty = Number(item.stock_local ?? item.availability?.[sessionMag] ?? 0);
    const outOfStock = localQty <= 0 || item.out_of_stock;
    const { eff, base, hasPromo, promo } = resolvePosCatalogPrice(item);
    addLineToCart({
      product_id: item.id,
      produit_ref: item.reference,
      designation: item.designation || item.reference,
      quantite: 1,
      prix_unitaire: eff,
      prix_total: eff,
      prix_vente_original: base,
      promo_applied: hasPromo,
      manual_promo: promo,
      stock_magasin_id: outOfStock ? null : (item.stock_magasin_id || sessionMag).toUpperCase(),
      thumbnail_url: item.thumbnail_url || null,
      is_hors_stock: outOfStock,
    });
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => {
      const next = [...prev];
      const qty = Math.max(1, next[idx].quantite + delta);
      next[idx] = { ...next[idx], quantite: qty, prix_total: qty * next[idx].prix_unitaire };
      return next;
    });
  };

  // Rabais « produit dégradé » : montant libre par ligne, sans plafond, distinct de la remise.
  const setRabais = (idx, montant, motif) => {
    setCart((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      const brut = Number(next[idx].prix_total || 0);
      const rab = Math.max(0, Math.min(Number(montant) || 0, brut));
      next[idx] = { ...next[idx], rabais_degat: rab, rabais_degat_motif: rab > 0 ? (motif || '') : '' };
      return next;
    });
  };

  const finalizeSale = async (effectiveMag, sumupCheckoutId = null, paypalOrderId = null) => {
    const encaissementMag = normMag(effectiveMag || sessionMag);
    let pe;
    let pc;
    let pa = 0;
    if (modePaiement === 'Avoir') {
      pa = parseFloat(paiementAvoir) || 0;
      const reste = Math.max(0, total - pa);
      if (complementMode === 'Espèces') {
        pe = parseFloat(paiementEspeces);
        if (!Number.isFinite(pe) || pe <= 0) pe = reste;
        pc = 0;
      } else {
        pc = parseFloat(paiementCb);
        if (!Number.isFinite(pc) || pc <= 0) pc = reste;
        pe = 0;
      }
    } else if (modePaiement === 'Mixte') {
      // Paiement mixte : espèces saisies, le reste sur le TPE si le champ SumUp est vide (audit 2026-09-09)
      pe = +((parseFloat(paiementEspeces) || 0).toFixed(2));
      const cbSaisi = parseFloat(paiementCb);
      pc = Number.isFinite(cbSaisi) && cbSaisi > 0 ? +(cbSaisi.toFixed(2)) : +(Math.max(0, total - pe).toFixed(2));
    } else if (modePaiement === 'Espèces') {
      pe = total;
    } else {
      pc = total;
    }
    const body = {
      items: cart.map((l) => ({
        produit_ref: l.produit_ref,
        designation: l.designation,
        quantite: l.quantite,
        prix_unitaire: l.prix_unitaire,
        prix_total: l.prix_total,
        rabais_degat: Number(l.rabais_degat || 0) || 0,
        rabais_degat_motif: l.rabais_degat_motif || '',
        mode_paiement: modePaiement,
        is_hors_stock: Boolean(l.is_hors_stock),
        stock_magasin_id: (l.is_acompte || l.is_hors_stock || l.produit_ref?.startsWith('ACOMPTE-'))
          ? null
          : (l.stock_magasin_id || encaissementMag),
      })),
      mode_paiement: modePaiement,
      client_nom: clientNom,
      client_tel: clientTel || undefined,
      magasin_id: encaissementMag,
      paiement_especes: pe,
      paiement_cb: pc,
      paiement_partiel: modePaiement === 'Mixte' || (modePaiement === 'Avoir' && pa < total),
      remise_pct: remiseType === 'pct' ? (Number(remiseValue) || 0) : 0,
      remise_montant: remiseType === 'montant' ? (Number(remiseValue) || 0) : 0,
    };
    if (modePaiement === 'Avoir') {
      body.avoir_ref = (avoirLookup?.ref || avoirRef || '').trim();
      body.paiement_avoir = pa;
    }
    if (sumupCheckoutId) body.sumup_checkout_id = sumupCheckoutId;
    if (paypalOrderId) body.paypal_order_id = paypalOrderId;
    const sent = await syncEngine.sendNow({
      method: 'POST',
      url: '/pos/v2/ventes',
      body,
      headers: { 'X-Magasin-Session-Id': encaissementMag },
    });
    const data = sent.data || {};
    const sale = { ...data, __offline: sent.offline };
    setLastSale(sale);
    setShowPrintPrompt(Boolean(sale.ref_vente));
    setCart([]);
    setPaiementEspeces('');
    setPaiementCb('');
    setAvoirRef('');
    setAvoirLookup(null);
    setAvoirSuggestions([]);
    setPaiementAvoir('');
    setRemiseValue(0);
    setRemiseType('pct');
    setLoyaltyRewardApplied(false);
    setSumupPending(null);
    setSumupStatus(null);
    setPaypalPending(null);
    setPaypalStatus(null);
    fetchCaisse();
    fetchDashboard();
  };

  const loadCaisseStatuses = useCallback(async () => {
    setLoadingCaisseByMag(true);
    const codes = (allMagasins || []).length
      ? allMagasins.map((m) => String(m.code).toUpperCase())
      : ['M1', 'M2'];
    const statuses = {};
    await Promise.all(codes.map(async (code) => {
      try {
        const res = await api.get('/caisse/status', { params: { magasin_id: code } });
        statuses[code] = res.data;
      } catch {
        statuses[code] = { is_open: false };
      }
    }));
    setCaisseByMag(statuses);
    setLoadingCaisseByMag(false);
    return statuses;
  }, [api, allMagasins]);

  const runSaleAfterMagConfirm = useCallback(async (targetMag) => {
    const mag = normMag(targetMag);
    setSessionMagasin(mag);
    setLoading(true);
    setError('');
    setLastSale(null);
    try {
      const res = await api.get('/caisse/status', { params: { magasin_id: mag } });
      setCaisse(res.data);
      if (!res.data?.is_open && offline.online) {
        setError(t('posV2.caisseClosedOpenFirst', { mag }));
        return;
      }
      const usesSumup = modePaiement === 'SumUp'
        || modePaiement === 'Mixte'
        || (modePaiement === 'Avoir' && complementMode === 'SumUp' && (parseFloat(paiementAvoir) || 0) < total);
      if (usesSumup && sumupPending && sumupStatus === 'PAID_PENDING') {
        await finalizeSale(mag, sumupPending.checkout_id);
        return;
      }
      if (usesSumup && !sumupPending) {
        const sumupAmount = modePaiement === 'SumUp'
          ? total
          : modePaiement === 'Avoir'
            ? Math.max(0, total - (parseFloat(paiementAvoir) || 0))
            : (modePaiement === 'Mixte' ? Math.max(0, +(total - (parseFloat(paiementEspeces) || 0)).toFixed(2)) : (parseFloat(paiementCb) || total));
        try {
          const checkoutRes = await api.post('/pos/v2/sumup/checkout', {
            amount: sumupAmount,
            magasin_id: mag,
            description: t('posV2.sumupCheckoutDesc', { count: cart.length }),
          });
          setSumupPending(checkoutRes.data);
          setSumupStatus(checkoutRes.data?.status || 'PENDING');
          setError('');
          return;
        } catch (sumupErr) {
          const detail = sumupErr.response?.data?.detail;
          const msg = typeof detail === 'string'
            ? detail
            : (sumupErr.message || t('posV2.sumupTerminalNotConnected'));
          throw new Error(t('posV2.sumupSaleNotSaved', { msg }));
        }
      }
      // PayPal — création de l'ordre puis, une fois capturé, finalisation de la vente.
      if (modePaiement === 'PayPal') {
        if (!offline.online) { setError('PayPal nécessite une connexion internet.'); return; }
        if (paypalPending && paypalStatus === 'COMPLETED') {
          await finalizeSale(mag, null, paypalPending.order_id);
          return;
        }
        if (!paypalPending) {
          try {
            const r = await api.post('/pos/v2/paypal/order', { amount: total, magasin_id: mag });
            setPaypalPending(r.data);
            setPaypalStatus(r.data?.status || 'CREATED');
            setError('');
          } catch (ppErr) {
            const d = ppErr.response?.data?.detail;
            throw new Error(typeof d === 'string' ? d : (ppErr.message || 'PayPal indisponible'));
          }
          return;
        }
        setError('Paiement PayPal en attente : faites approuver le client puis capturez.');
        return;
      }
      await finalizeSale(mag, null);
    } catch (err) {
      if (isRemisePlafonneeError(err)) {
        setRemiseValue((prev) => clampRemise(prev));
        setError(`${t('posV2.remisePlafonneeMsg', { pct: REMISE_MAX_PCT })} ${t('posV2.venteNonEnregRemiseRamenee')}`);
        return;
      }
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'object' ? JSON.stringify(detail, null, 2) : (detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [api, sessionMag, setSessionMagasin, offline.online, modePaiement, sumupPending, sumupStatus, paypalPending, paypalStatus, total, cart.length, clampRemise]); // eslint-disable-line react-hooks/exhaustive-deps

  const capturePaypal = useCallback(async () => {
    if (!paypalPending?.order_id) return;
    setPaypalBusy(true); setError('');
    try {
      const r = await api.post(`/pos/v2/paypal/order/${paypalPending.order_id}/capture`);
      if (r.data?.status === 'COMPLETED') setPaypalStatus('COMPLETED');
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e.message || 'Capture PayPal échouée'));
    } finally { setPaypalBusy(false); }
  }, [api, paypalPending]);

  const cancelPaypal = useCallback(() => { setPaypalPending(null); setPaypalStatus(null); setError(''); }, []);

  // Polling du statut PayPal pendant l'approbation du client.
  useEffect(() => {
    if (!paypalPending?.order_id || paypalStatus === 'COMPLETED') return undefined;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/pos/v2/paypal/order/${paypalPending.order_id}/status`);
        if (!alive) return;
        if (r.data?.captured) setPaypalStatus('COMPLETED');
        else if (r.data?.status) setPaypalStatus(r.data.status);
      } catch (_) { /* noop */ }
    }, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [paypalPending, paypalStatus, api]);

  // Changement de mode de paiement -> réinitialise un ordre PayPal en cours (montant périmé).
  useEffect(() => { setPaypalPending(null); setPaypalStatus(null); }, [modePaiement]);

  const openMagConfirmModal = useCallback(async () => {
    setSelectedSaleMagasin(sessionMag);
    setShowMagConfirm(true);
    await loadCaisseStatuses();
  }, [sessionMag, loadCaisseStatuses]);

  const confirmMagAndSale = async () => {
    setShowMagConfirm(false);
    await runSaleAfterMagConfirm(selectedSaleMagasin);
  };

  const submitSale = async () => {
    if (!cart.length) { setError(t('posV2.cartEmpty')); return; }
    // Garde finale plafond remise avant ouverture du modal de paiement.
    if (!carlosUser && subtotal > 0 && remiseAmount > subtotal * (REMISE_MAX_PCT / 100) + 0.005) {
      setRemiseValue((prev) => clampRemise(prev));
      setError(`${t('posV2.remisePlafonneeMsg', { pct: REMISE_MAX_PCT })} ${t('posV2.remiseRameneeVerif')}`);
      return;
    }
    if (modePaiement === 'Avoir' && !avoirLookup?.ref) {
      setError(t('posV2.lookupValidAvoir'));
      return;
    }
    if (sumupPending && sumupStatus === 'PAID_PENDING') {
      await runSaleAfterMagConfirm(sessionMag);
      return;
    }
    if (sumupPending) return;
    await openMagConfirmModal();
  };

  const cancelSumup = async () => {
    if (sumupPending?.checkout_id) {
      try {
        await api.post(`/pos/v2/sumup/cancel/${sumupPending.checkout_id}`);
      } catch {
        /* local clear even if API fails */
      }
    }
    setSumupPending(null);
    setSumupStatus(null);
  };

  const printTicket = async ({ closePrompt = false } = {}) => {
    if (!lastSale?.ref_vente || lastSale.__offline) return;
    setPrinting(true);
    try {
      await printTicket80mm(api, { type: 'vente', ref: lastSale.ref_vente });
      if (closePrompt) setShowPrintPrompt(false);
    } catch (err) {
      setError(errMessage(err, t('posV2.printError')));
    } finally {
      setPrinting(false);
    }
  };

  const caisseOpen = caisse?.is_open !== false && caisse?.id;
  const totalPages = Math.max(1, Math.ceil((catalog.total || 0) / (catalog.limit || 48)));

  return (
    <Layout>
      <div className="min-h-screen bg-[#0f1117] text-white pb-[88px]">
        <PosV2OfflineBanner
          online={offline.online}
          pending={offline.pending}
          syncing={offline.syncing}
          onRetry={() => syncEngine.flush()}
        />
        <PosV2Header
          sessionMag={sessionMag}
          onSessionMagChange={handleSessionMagChange}
          cartCount={cart.length}
          vendeur={user?.username}
          caisseOpen={caisseOpen}
          activeMode={activeMode}
          onModeChange={handleModeChange}
          healthPhase={health?.phase}
          onOpenTickets={() => setDrawer('tickets')}
          posV2Preferred={posV2Preferred}
          onTogglePreferred={togglePosV2Preferred}
        />

        {!caisseOpen && offline.online && (
          <div className="mx-4 mt-2 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-950/30 px-3 py-2 text-amber-100 text-sm">
            <AlertTriangle size={16} />
            {t('posV2.caisseClosedBanner', { mag: sessionMag })}
            <Link to="/open-caisse" className="underline font-semibold ml-1">{t('posV2.open')}</Link>
          </div>
        )}
        {crossMagasin && (
          <div className="mx-4 mt-2 text-xs text-amber-200 bg-amber-950/20 border border-amber-500/30 rounded-lg px-3 py-1.5">
            {t('posV2.oneEncaissementMultiStock', { mag: sessionMag })}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,28%)_minmax(0,1fr)_minmax(0,32%)] gap-3 p-3 max-w-[1600px] mx-auto min-h-[calc(100vh-200px)] min-w-0">
          <section className="rounded-xl border border-white/10 bg-[#151820] p-3 flex flex-col">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wide">{t('posV2.catalog')}</h2>
              <span className="text-[10px] text-gray-500">{t('posV2.ficheButtonHint')}</span>
            </div>
            <div className="flex gap-2 mb-2" data-testid="pos-catalog-magasin-toggle">
              {[
                { code: 'ALL', label: t('posV2.all') },
                { code: 'M1', label: t('posV2.stockMag', { mag: 'M1' }) },
                { code: 'M2', label: t('posV2.stockMag', { mag: 'M2' }) },
              ].map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => selectCatalogShop(code)}
                  className={`flex-1 min-h-[40px] rounded-lg text-xs font-bold border transition-colors ${
                    catalogMag === code
                      ? 'border-red-500/50 bg-red-950/30 text-white'
                      : 'border-white/10 text-gray-400 hover:border-white/20 hover:text-gray-200'
                  }`}
                  data-testid={`pos-catalog-mag-${code}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                <input
                  className="w-full pl-8 pr-2 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm min-h-[44px] text-white placeholder:text-gray-500"
                  placeholder={t('posV2.catalogPlaceholder')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadCatalog(1)}
                />
              </div>
              <button type="button" onClick={() => loadCatalog(1)} disabled={loadingCatalog}
                className="px-3 min-h-[44px] rounded-lg bg-red-600 font-semibold text-sm">
                {loadingCatalog ? <Loader2 className="animate-spin" size={16} /> : t('posV2.go')}
              </button>
            </div>
            <div className="flex items-center justify-between text-[10px] text-gray-500 mb-2">
              <span>{t('posV2.catalogMeta', { total: catalog.total, scope: catalogMag === 'ALL' ? 'M1+M2' : catalogMag, page: catalog.page, pages: totalPages })}</span>
              <div className="flex gap-1">
                <button type="button" disabled={catalog.page <= 1} onClick={() => loadCatalog(catalog.page - 1)}
                  className="p-1 rounded bg-white/10 disabled:opacity-30"><ChevronLeft size={14} /></button>
                <button type="button" disabled={catalog.page >= totalPages} onClick={() => loadCatalog(catalog.page + 1)}
                  className="p-1 rounded bg-white/10 disabled:opacity-30"><ChevronRight size={14} /></button>
              </div>
            </div>
            <PosV2CatalogGrid
              items={catalog.items || []}
              sessionMag={catalogMag === 'ALL' ? sessionMag : catalogMag}
              photoBust={catalogPhotoBust}
              onAdd={addFromCatalog}
              onDetail={(item) => { setProductDetail(item); setDrawer('product'); }}
            />
          </section>

          <PosV2CartPanel cart={cart} lineBadges={lineBadges} onUpdateQty={updateQty} onRemove={(i) => setCart((p) => p.filter((_, x) => x !== i))} onSetRabais={setRabais} />

          <PosV2CheckoutPanel
            clientNom={clientNom}
            onClientChange={setClientNom}
            clientTel={clientTel}
            clientLoyalty={clientLoyalty}
            onOpenClient={() => { setActiveMode('client'); setDrawer('client'); }}
            modePaiement={modePaiement}
            onModeChange={setModePaiement}
            paiementEspeces={paiementEspeces}
            onEspecesChange={setPaiementEspeces}
            paiementCb={paiementCb}
            onCbChange={setPaiementCb}
            subtotal={subtotal}
            remiseType={remiseType}
            onRemiseTypeChange={setRemiseType}
            remiseValue={remiseValue}
            onRemiseValueChange={setRemiseValueClamped}
            remiseMaxHint={!carlosUser}
            carlosUser={carlosUser}
            remiseAmount={remiseAmount}
            total={total}
            loading={loading}
            error={typeof error === 'string' ? error : (error && (error.message || error.code)) || ''}
            lastSale={lastSale}
            isOnline={offline.online}
            onSubmit={submitSale}
            onPrint={() => printTicket()}
            printing={printing}
            sumupPending={sumupPending}
            sumupStatus={sumupStatus}
            onCancelSumup={cancelSumup}
            paypalPending={paypalPending}
            paypalStatus={paypalStatus}
            paypalBusy={paypalBusy}
            onCapturePaypal={capturePaypal}
            onCancelPaypal={cancelPaypal}
            avoirRef={avoirRef}
            onAvoirRefChange={(v) => {
              setAvoirRef(v);
              if (avoirLookup && v.trim() !== avoirLookup.ref) setAvoirLookup(null);
            }}
            onLookupAvoir={async () => {
              const q = (avoirRef || '').trim();
              if (q.length < 2) { setError(t('posV2.enterAvoirRefOrClient')); return; }
              setAvoirLoading(true);
              setError('');
              try {
                const res = await api.get('/avoirs/lookup', { params: { q } });
                if (Array.isArray(res.data?.matches)) {
                  if (res.data.matches.length === 1) applyAvoirLookup(res.data.matches[0]);
                  else setAvoirSuggestions(res.data.matches);
                } else {
                  applyAvoirLookup(res.data);
                }
              } catch (err) {
                setAvoirLookup(null);
                setAvoirSuggestions([]);
                setError(errMessage(err, t('posV2.avoirNotFound')));
              } finally {
                setAvoirLoading(false);
              }
            }}
            onSelectAvoir={applyAvoirLookup}
            avoirLookup={avoirLookup}
            avoirLoading={avoirLoading}
            avoirSuggestions={avoirSuggestions}
            paiementAvoir={paiementAvoir}
            onPaiementAvoirChange={setPaiementAvoir}
            complementMode={complementMode}
            onComplementModeChange={setComplementMode}
          />
        </div>
      </div>

      <PosV2CaisseFooter dashboard={dashboard} sessionMag={sessionMag} />

      <PosV2EncaissementMagModal
        open={showMagConfirm}
        onClose={() => setShowMagConfirm(false)}
        allMagasins={allMagasins}
        sessionMag={sessionMag}
        selectedMag={selectedSaleMagasin}
        onSelectMag={setSelectedSaleMagasin}
        onConfirm={confirmMagAndSale}
        cartStockByMag={cartStockByMag}
        crossMagasin={crossMagasin}
        total={total}
        remiseAmount={remiseAmount}
        caisseByMag={caisseByMag}
        loadingCaisse={loadingCaisseByMag}
        loading={loading}
      />

      <PosV2PrintTicketModal
        open={showPrintPrompt}
        sale={lastSale}
        printing={printing}
        onPrint={() => printTicket({ closePrompt: true })}
        onSkip={() => setShowPrintPrompt(false)}
      />

      <PosV2Drawer open={drawer === 'product'} modal title={t('posV2.productSheet')} subtitle={t('posV2.productSheetSub')} onClose={() => { setDrawer(null); setProductDetail(null); }}>
        <PosV2ProductDrawer
          api={api}
          product={productDetail}
          sessionMag={sessionMag}
          onAdd={addFromCatalog}
          onClose={() => { setDrawer(null); setProductDetail(null); }}
          onPhotoUpdated={handlePhotoUpdated}
        />
      </PosV2Drawer>

      <PosV2Drawer
        open={drawer === 'tickets'}
        modal
        title={t('posV2.ticketCenter')}
        subtitle={t('posV2.ticketCenterSub')}
        onClose={() => setDrawer(null)}
      >
        <PosV2TicketsDrawer api={api} sessionMag={sessionMag} onClose={() => setDrawer(null)} />
      </PosV2Drawer>

      <PosV2Drawer
        open={drawer === 'sav'}
        modal
        title={t('posV2.savStation')}
        subtitle={t('posV2.savStationSub')}
        onClose={() => { setDrawer(null); setActiveMode('vente'); }}
      >
        <PosV2SavDrawer
          api={api}
          sessionMag={sessionMag}
          onAddLine={addLineToCart}
          onAttachClient={({ nom, telephone }) => {
            if (nom) setClientNom(nom);
            if (telephone) setClientTel(telephone);
          }}
          onClose={() => { setDrawer(null); setActiveMode('vente'); }}
        />
      </PosV2Drawer>

      <PosV2Drawer
        open={drawer === 'hors_stock'}
        modal
        title={t('posV2.horsStock')}
        subtitle={t('posV2.horsStockSub')}
        onClose={() => { setDrawer(null); setActiveMode('vente'); }}
      >
        <PosV2HorsStockDrawer
          api={api}
          sessionMag={sessionMag}
          carlosUser={carlosUser}
          onAddLine={addLineToCart}
          onClose={() => { setDrawer(null); setActiveMode('vente'); }}
        />
      </PosV2Drawer>

      <PosV2Drawer
        open={drawer === 'client'}
        modal
        title={t('posV2.clientCenter')}
        subtitle={t('posV2.clientCenterSub')}
        onClose={() => { setDrawer(null); setActiveMode('vente'); }}
      >
        <PosV2ClientDrawer
          api={api}
          onSelectClient={({ nom, telephone, loyalty, remise_pct: clientRemisePct }) => {
            setClientNom(nom || clientNom);
            setClientTel(telephone || '');
            setClientLoyalty(loyalty || null);
            if (loyalty?.pending_reward && !loyaltyRewardApplied) {
              setRemiseType('pct');
              setRemiseValue(15);
              setLoyaltyRewardApplied(true);
            } else if (clientRemisePct > 0) {
              // Remise fiche client plafonnée à 20 % pour les vendeurs (la récompense fidélité 15 % reste intacte).
              setRemiseType('pct');
              setRemiseValue(clampRemise(clientRemisePct, 'pct'));
            }
          }}
          onClose={() => { setDrawer(null); setActiveMode('vente'); }}
        />
      </PosV2Drawer>

      <PosV2Drawer
        open={drawer === 'commandes'}
        modal
        title={t('posV2.orders')}
        subtitle={t('posV2.ordersSub')}
        onClose={() => { setDrawer(null); setActiveMode('vente'); }}
      >
        <PosV2CommandesDrawer
          api={api}
          sessionMag={sessionMag}
          onAddLine={addLineToCart}
          onClose={() => { setDrawer(null); setActiveMode('vente'); }}
        />
      </PosV2Drawer>
    </Layout>
  );
};

export default PosV2Page;
